'use client'

import dynamic from 'next/dynamic'
import { Component, useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { renderTier, type RenderTier } from '@/components/product/studio/render-tier'
import styles from './AtelierBottleStage.module.css'

/**
 * Progressive stage for the atelier bottle. The server (and every jsdom test)
 * renders the 2D bottle render with the engraving caption — the complete
 * experience with no JavaScript, no WebGL, or Save-Data. Capable browsers
 * upgrade in place to the live 3D bottle, which paints the label and the
 * visitor's engraving onto the model itself; the caption then retires because
 * the engraving is visible on the bottle. The canvas region stays aria-hidden:
 * everything it shows (gem, month, line, poem, engraving) already exists as
 * text in the surrounding card.
 *
 * The still is never traded for a promise. It stays mounted and lit through the
 * whole loading window and retires only once the renderer reports a frame it
 * has actually drawn; a failure at any point after the upgrade decision — a GLB
 * that will not load, a broken export, a context the driver takes back — brings
 * it straight back. There is nothing to retry and nothing to time out: the
 * still IS the card, so falling back to it costs the visitor nothing.
 */

const BirthstoneBottleScene = dynamic(() => import('@/components/product/BirthstoneBottleScene'), {
  ssr: false,
})

type Media = { avif: string; webp: string; src: string; width: number; height: number }

type Props = {
  gem: string
  month: string
  line: 'femme' | 'homme'
  color: string
  engraving: string
  media: Media
  alt: string
}

/**
 * Live-bottle readiness. `still` is the un-upgraded card, `loading` a canvas
 * that has not painted yet, `fallback` a canvas that failed and will not be
 * mounted again for this card. Only `ready` shows the canvas, so `data-bottle`
 * reports a drawn frame rather than a browser capability.
 */
type StageState = 'still' | 'loading' | 'ready' | 'fallback'

/**
 * CPU rasterisers (headless CI, VMs without GPU passthrough, machines that fell
 * back from a blocklisted driver) report themselves here — the same list the
 * hero screens on. They can create a context and then need tens of seconds to
 * compile a PBR shader, which on this card would mean a canvas that sits blank
 * long after the visitor has scrolled to it. The authored render is both faster
 * and better on those machines.
 */
const SOFTWARE_RENDERER = /swiftshader|llvmpipe|softpipe|software rasterizer|microsoft basic render/i

function liveBottleUsable(): boolean {
  try {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('webgl2') ?? canvas.getContext('webgl')
    let usable = false
    if (context) {
      const info = context.getExtension('WEBGL_debug_renderer_info')
      const renderer = info === null ? '' : String(context.getParameter(info.UNMASKED_RENDERER_WEBGL))
      usable = !SOFTWARE_RENDERER.test(renderer)
    }
    // The probe must not keep a context alive: the hero runs a canvas of its
    // own and browsers cap the number of live contexts per document.
    context?.getExtension('WEBGL_lose_context')?.loseContext()
    return usable
  } catch {
    return false
  }
}

function saveDataRequested(): boolean {
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
  return Boolean(connection?.saveData)
}

/**
 * The GLB and the WebGL context are the two things that can fail after the
 * upgrade decision, and a rejected asset surfaces as a throw during render
 * (R3F re-raises it outside its canvas). Uncaught, that throw takes the whole
 * collection chapter down with it — so the live bottle renders behind a
 * boundary that reports the failure and unmounts itself, leaving the still.
 */
class BottleBoundary extends Component<{ onFailure: (reason: string) => void; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  override componentDidCatch(error: Error) {
    this.props.onFailure(`render:${error.message.slice(0, 120)}`)
  }

  override render() {
    return this.state.failed ? null : this.props.children
  }
}

export default function AtelierBottleStage({ gem, month, line, color, engraving, media, alt }: Props) {
  const figureRef = useRef<HTMLElement>(null)
  const [enhanced, setEnhanced] = useState(false)
  const [stage, setStage] = useState<StageState>('still')
  const [motionOk, setMotionOk] = useState(false)
  const [inView, setInView] = useState(false)
  const [documentVisible, setDocumentVisible] = useState(true)
  // The render tier (studio or lite), decided once with the hero and reported
  // as `data-tier` so verifiers can tell which family painted.
  const [tier, setTier] = useState<RenderTier | null>(null)

  useEffect(() => {
    if (!liveBottleUsable() || saveDataRequested()) return
    setTier(renderTier())
    setEnhanced(true)
    setStage('loading')
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const applyMotion = () => setMotionOk(!query.matches)
    applyMotion()
    query.addEventListener('change', applyMotion)
    return () => query.removeEventListener('change', applyMotion)
  }, [])

  useEffect(() => {
    if (!enhanced) return
    const target = figureRef.current
    if (!target) return
    const observer = new IntersectionObserver(([entry]) => setInView(Boolean(entry?.isIntersecting)), {
      rootMargin: '80px',
    })
    observer.observe(target)
    const onVisibility = () => setDocumentVisible(document.visibilityState === 'visible')
    onVisibility()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      observer.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enhanced])

  /**
   * Every failure is terminal for this card. A fresh canvas would be the cure
   * for a merely lost context, but the still is the complete content, and the
   * hero already holds a context on this page: quietly keeping the authored
   * render is the honest answer, not a queue of retries.
   */
  const onFailure = useCallback((reason: string) => {
    setStage('fallback')
    if (process.env.NODE_ENV !== 'production') console.warn(`[AtelierBottleStage] live bottle unavailable: ${reason}`)
  }, [])

  const onReady = useCallback(() => setStage((current) => (current === 'fallback' ? current : 'ready')), [])

  const live = enhanced && stage !== 'fallback'
  // The canvas is mounted only while the card is near the viewport of a
  // foregrounded tab: the hero holds a live context of its own, and browsers cap
  // live contexts per document. Scrolling away restores the still, which is the
  // same complete content the server rendered.
  const mounted = live && inView && documentVisible
  const ready = live && stage === 'ready'

  // Unmounting takes the canvas — and therefore the drawn frame — with it, so
  // readiness has to go too: the next mount must paint before the still steps
  // aside again, otherwise coming back onto screen would flash an empty stage.
  useEffect(() => {
    if (mounted || stage !== 'ready') return
    setStage('loading')
  }, [mounted, stage])

  return (
    <figure ref={figureRef} className="atelier__stage" data-bottle={ready ? '3d' : 'still'} data-tier={tier ?? undefined}>
      <picture className={styles.still}>
        <source srcSet={media.avif} type="image/avif" />
        <source srcSet={media.webp} type="image/webp" />
        <img className="atelier__bottle" src={media.src} alt={alt} width={media.width} height={media.height} />
      </picture>
      {engraving ? (
        <figcaption className={`atelier__engraving ${styles.caption}`} aria-hidden="true">
          {engraving}
        </figcaption>
      ) : null}

      {mounted ? (
        <div className={`atelier__canvas ${styles.canvas}`} data-visible={ready ? 'true' : undefined} aria-hidden="true">
          <BottleBoundary onFailure={onFailure}>
            <BirthstoneBottleScene
              gem={gem}
              month={month}
              line={line}
              color={color}
              engraving={engraving}
              motionOk={motionOk}
              inView={inView}
              tier={tier ?? 'lite'}
              onReady={onReady}
              onFailure={onFailure}
            />
          </BottleBoundary>
        </div>
      ) : null}
    </figure>
  )
}
